import { createAsyncThunk } from "@reduxjs/toolkit";
import { axios } from "config/api";
import { CreateUserRequest, EditUserRequest } from "interfaces/requests/Users";
import {
  FirebaseAuthErrorCode,
  firebaseErrorMessages,
} from "libraries/firebase/errorMessages";
import { getURLWithQueryParams } from "utils/url";

const BASE_URL = "/user";

export const fetchUsers = createAsyncThunk<
  string,
  { searchParam: string; isActiveParam: string }
>("user/get", async ({ searchParam = "", isActiveParam = "" }) => {
  try {
    const url = getURLWithQueryParams(BASE_URL, {
      search: searchParam,
      isActive: isActiveParam,
    });
    const response = await axios.get(url);
    return response.data.data;
  } catch (error: any) {
    console.log("Error - fetchUsers ->", error);
    throw new Error(error.message);
  }
});

export const fetchUserById = createAsyncThunk(
  "user/getById",
  async (userId: string, { rejectWithValue }) => {
    try {
      const url = `${BASE_URL}/${userId}`;
      const response = await axios.get(url);

      return response.data;
    } catch (error: any) {
      console.error("Error - fetchUserById ->", error);
      return rejectWithValue(error.response?.data || "Error fetching user");
    }
  },
);

export const addUser = createAsyncThunk(
  "users/add",
  async (request: CreateUserRequest, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}`, request);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        const errorCode = error.response.data?.code as FirebaseAuthErrorCode;

        const errorMessage = firebaseErrorMessages[errorCode];

        return rejectWithValue({ code: errorCode, message: errorMessage });
      } else {
        throw new Error("Unexpected error: " + error.message);
      }
    }
  },
);

export const activateUser = createAsyncThunk(
  "users/activate",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`${BASE_URL}/activate/${id}`);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error);
      } else {
        throw new Error("Unexpected error: " + error.message);
      }
    }
  },
);

export const deactivateUser = createAsyncThunk(
  "users/deactivate",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`${BASE_URL}/deactivate/${id}`);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error);
      } else {
        throw new Error("Unexpected error: " + error.message);
      }
    }
  },
);

export const editUser = createAsyncThunk(
  "users/edit",
  async (request: EditUserRequest, { rejectWithValue }) => {
    try {
      const { id, ...data } = request;
      const url = `${BASE_URL}/${id}`;
      const response = await axios.patch(url, data);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        const errorCode = error.response.data?.code as FirebaseAuthErrorCode;

        const errorMessage = firebaseErrorMessages[errorCode];

        return rejectWithValue({ code: errorCode, message: errorMessage });
      } else {
        throw new Error("Unexpected error: " + error.message);
      }
    }
  },
);
